import React, {
  useState,
  useEffect,
} from 'react';
import {
  useParams,
  useNavigate,
} from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemSecondaryAction,
  InputAdornment,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  SportsEsports as GameIcon,
  TableChart as SeatIcon,
  PersonAdd as RegisterIcon,
  PersonRemove as UnregisterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Payment as PaymentIcon,
  Save as SaveIcon,
  RemoveCircle as RemoveParticipantIcon,
} from '@mui/icons-material';
import {
  useAuth,
} from '../../contexts/AuthContext';
import {
  eventsService,
} from '../../services/events';
import { resolveMediaUrl } from '../../utils/mediaUrl';

// Venue SVG Components
const VenueBar = ({ theme }) => (
  <Box
    sx={{
      width: '100%',
      height: { xs: 60, sm: 80 },
      backgroundColor: '#241E18',
      border: `1px solid ${alpha('#3D7A8C', 0.3)}`,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      mb: 1
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 400 80" preserveAspectRatio="xMidYMid meet">
      <rect x="10" y="30" width="380" height="40" rx="5" 
        fill={alpha('#3D7A8C', 0.3)} 
        stroke="#3D7A8C" 
        strokeWidth="2" />
      {[60, 120, 180, 240, 300, 340].map((x, i) => (
        <g key={i}>
          <circle cx={x} cy="25" r="8" fill={alpha('#C9A227', 0.4)} stroke="#C9A227" strokeWidth="1.5" />
          <line x1={x} y1="25" x2={x} y2="35" stroke="#C9A227" strokeWidth="2" />
        </g>
      ))}
      <text x="200" y="55" textAnchor="middle" fill="#3D7A8C" fontSize="24" fontWeight="bold">
        BAR
      </text>
    </svg>
  </Box>
);

const VenueStage = ({ theme }) => (
  <Box
    sx={{
      width: '100%',
      height: { xs: 60, sm: 80 },
      backgroundColor: '#3A3018',
      border: `1px solid ${alpha('#C47B5A', 0.3)}`,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      mt: 1
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 400 80" preserveAspectRatio="xMidYMid meet">
      <rect x="20" y="15" width="360" height="50" rx="8" 
        fill={alpha('#C47B5A', 0.3)} 
        stroke="#C47B5A" 
        strokeWidth="2" />
      <circle cx="100" cy="40" r="15" fill={alpha('#C9A227', 0.2)} />
      <circle cx="200" cy="40" r="20" fill={alpha('#C9A227', 0.3)} />
      <circle cx="300" cy="40" r="15" fill={alpha('#C9A227', 0.2)} />
      <text x="200" y="48" textAnchor="middle" fill="#C47B5A" fontSize="24" fontWeight="bold">
        STAGE
      </text>
    </svg>
  </Box>
);

const VenueToiletsFemale = ({ theme }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      minHeight: { xs: 60, sm: 80 },
      backgroundColor: '#3A2A22',
      border: `1px solid ${alpha('#C47B5A', 0.3)}`,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet">
      <circle cx="50" cy="20" r="6" fill="#C47B5A" />
      <line x1="50" y1="26" x2="50" y2="42" stroke="#C47B5A" strokeWidth="3" />
      <path d="M 38 32 L 50 32 L 62 32 L 62 42 L 50 42 L 38 42 Z" 
        fill={alpha('#C47B5A', 0.3)} 
        stroke="#C47B5A" 
        strokeWidth="2" />
      <line x1="50" y1="42" x2="42" y2="55" stroke="#C47B5A" strokeWidth="3" />
      <line x1="50" y1="42" x2="58" y2="55" stroke="#C47B5A" strokeWidth="3" />
      <text x="50" y="70" textAnchor="middle" fill="#C47B5A" fontSize="14" fontWeight="bold">
        WC ♀
      </text>
    </svg>
  </Box>
);

const VenueToiletsMale = ({ theme }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      minHeight: { xs: 60, sm: 80 },
      backgroundColor: '#1E2C31',
      border: `1px solid ${alpha('#3D7A8C', 0.3)}`,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet">
      <circle cx="50" cy="20" r="6" fill="#3D7A8C" />
      <line x1="50" y1="26" x2="50" y2="45" stroke="#3D7A8C" strokeWidth="3" />
      <line x1="50" y1="32" x2="42" y2="40" stroke="#3D7A8C" strokeWidth="3" />
      <line x1="50" y1="32" x2="58" y2="40" stroke="#3D7A8C" strokeWidth="3" />
      <line x1="50" y1="45" x2="42" y2="55" stroke="#3D7A8C" strokeWidth="3" />
      <line x1="50" y1="45" x2="58" y2="55" stroke="#3D7A8C" strokeWidth="3" />
      <text x="50" y="70" textAnchor="middle" fill="#3D7A8C" fontSize="14" fontWeight="bold">
        WC ♂
      </text>
    </svg>
  </Box>
);

const VenueChillout = ({ theme }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      minHeight: { xs: 60, sm: 80 },
      backgroundColor: '#243028',
      border: `1px solid ${alpha('#6B8F71', 0.3)}`,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet">
      <rect x="15" y="30" width="70" height="25" rx="4" 
        fill={alpha('#6B8F71', 0.3)} 
        stroke="#6B8F71" 
        strokeWidth="2" />
      <rect x="15" y="20" width="70" height="12" rx="4" 
        fill={alpha('#6B8F71', 0.4)} 
        stroke="#6B8F71" 
        strokeWidth="1.5" />
      <rect x="12" y="25" width="8" height="30" rx="2" 
        fill={alpha('#6B8F71', 0.4)} 
        stroke="#6B8F71" 
        strokeWidth="1.5" />
      <rect x="80" y="25" width="8" height="30" rx="2" 
        fill={alpha('#6B8F71', 0.4)} 
        stroke="#6B8F71" 
        strokeWidth="1.5" />
      <text x="50" y="72" textAnchor="middle" fill="#6B8F71" fontSize="14" fontWeight="bold">
        CHILL
      </text>
    </svg>
  </Box>
);

function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectingSeat, setSelectingSeat] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    payment_status: 'pending',
    payment_method: '',
    payment_amount: ''
  });
  const [unregisterDialog, setUnregisterDialog] = useState(false);
  const [removeParticipantDialog, setRemoveParticipantDialog] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState(null);
  const [removingParticipant, setRemovingParticipant] = useState(false);

  const currentUserId = user?.id || user?._id;
  const isRegistered = event && event.participants.includes(currentUserId);
  const isOrganizer = event && event.organizer_id === currentUserId;
  const isAdmin = user?.role === 'admin';
  const hasAdminAccess = isOrganizer || isAdmin;
  const isFull = event && event.participants.length >= event.max_participants;
  const hasEventStarted = event && new Date(event.end_date) < new Date();
  const isEventOngoing = event && new Date(event.start_date) <= new Date() && new Date() <= new Date(event.end_date);
  
  // Check if current user has paid
  const currentUserParticipant = event && event.participants.find(p => p.user_id === currentUserId);
  const isUserPaid = currentUserParticipant?.payment_status === 'paid';
  
  // Find current user's seat assignment
  const currentUserSeat = event && event.seatplan.assignments.find(
    assignment => assignment.user_id === currentUserId
  );

  useEffect(() => {
    loadEvent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (event && event.participants.length > 0) {
      loadParticipants();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const loadEvent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await eventsService.getEvent(eventId);
      if (result.success) {
        setEvent(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading event:', err);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    setLoadingParticipants(true);
    
    try {
      const result = await eventsService.getEventParticipants(eventId);
      if (result.success) {
        // Transform backend data to include seat assignments
        const participantDetails = result.data.map(participant => ({
          ...participant,
          id: participant.user_id,
          seat_assignment: event.seatplan.assignments.find(
            assignment => assignment.user_id === participant.user_id
          )
        }));
        setParticipants(participantDetails);
      } else {
        console.error('Failed to load participants:', result.error);
        // Don't set error for participants as it's not critical
      }
    } catch (err) {
      console.error('Error loading participants:', err);
      // Don't set error for participants as it's not critical
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const result = await eventsService.registerForEvent(eventId);
      if (result.success) {
        setEvent(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error registering for event:', err);
      setError('Failed to register for event');
    } finally {
      setRegistering(false);
    }
  };

  const handleUnregister = async () => {
    setRegistering(true);
    setUnregisterDialog(false);
    try {
      const result = await eventsService.unregisterFromEvent(eventId);
      if (result.success) {
        await loadEvent(); // Reload event data
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error unregistering from event:', err);
      setError('Failed to unregister from event');
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await eventsService.deleteEvent(eventId);
      if (result.success) {
        navigate('/events');
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const handleSeatSelect = async (row, column) => {
    if (!isRegistered || selectingSeat) return;
    
    // Check if user has paid (organizers and admins can bypass this check)
    if (!hasAdminAccess && !isUserPaid) {
      setError('You must complete payment to select a seat. Please contact an organizer to process your payment.');
      return;
    }
    
    setSelectingSeat(true);
    setError(null);
    
    try {
      const result = await eventsService.assignSeat(eventId, row, column);
      if (result.success) {
        // Reload event data to get updated seat assignments
        await loadEvent();
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error selecting seat:', err);
      setError('Failed to select seat');
    } finally {
      setSelectingSeat(false);
    }
  };

  const handleSeatRemove = async () => {
    if (!isRegistered || !currentUserSeat || selectingSeat) return;
    
    setSelectingSeat(true);
    setError(null);
    
    try {
      const result = await eventsService.removeSeatAssignment(eventId);
      if (result.success) {
        // Reload event data to get updated seat assignments
        await loadEvent();
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error removing seat:', err);
      setError('Failed to remove seat assignment');
    } finally {
      setSelectingSeat(false);
    }
  };

  const handlePaymentEdit = (participant) => {
    setSelectedParticipant(participant);
    setPaymentFormData({
      payment_status: participant.payment_status || 'pending',
      payment_method: participant.payment_method || '',
      payment_amount: participant.payment_amount || ''
    });
    setPaymentDialog(true);
  };

  const handlePaymentUpdate = async () => {
    if (!selectedParticipant) return;

    setUpdatingPayment(true);
    try {
      const paymentData = {
        user_id: selectedParticipant.user_id,
        payment_status: paymentFormData.payment_status,
        payment_date: paymentFormData.payment_status === 'paid' ? new Date().toISOString() : null,
        payment_method: paymentFormData.payment_method || null,
        payment_amount: paymentFormData.payment_amount ? parseFloat(paymentFormData.payment_amount) : null
      };

      const result = await eventsService.updatePaymentStatus(eventId, selectedParticipant.user_id, paymentData);
      
      if (result.success) {
        // Reload participants to get updated data
        await loadParticipants();
        setPaymentDialog(false);
        setSelectedParticipant(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Failed to update payment status');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!participantToRemove) return;
    
    setRemovingParticipant(true);
    setRemoveParticipantDialog(false);
    
    try {
      const result = await eventsService.removeEventParticipant(eventId, participantToRemove.user_id);
      if (result.success) {
        await loadEvent(); // Reload event data
        await loadParticipants(); // Reload participants
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error removing participant:', err);
      setError('Failed to remove participant');
    } finally {
      setRemovingParticipant(false);
      setParticipantToRemove(null);
    }
  };

  const formatDateRange = (startDateString, endDateString) => {
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
    
    const startFormatted = startDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const endFormatted = endDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'primary';
      case 'ongoing': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Event not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={() => navigate('/events')}
        >
          Back to Events
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/events')}
        sx={{ mb: 3 }}
      >
        Back to Events
      </Button>

      {/* Hero Image */}
      {event.image_url && (
        <Box
          sx={{
            width: '100%',
            height: { xs: 250, md: 400 },
            mb: 4,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            background: `url(${resolveMediaUrl(event.image_url)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(to bottom, transparent 0%, ${alpha('#000', 0.8)} 100%)`
            }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 16, md: 32 },
              left: { xs: 16, md: 32 },
              right: { xs: 16, md: 32 },
              zIndex: 1,
              color: 'white'
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
                textShadow: '0 4px 8px rgba(0,0,0,0.5)',
                mb: 1
              }}
            >
              {event.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={isEventOngoing ? 'Ongoing' : event.status}
                color={isEventOngoing ? 'warning' : getStatusColor(event.status)}
                size="medium"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                }}
              />
              {event.game && (
                <Chip
                  icon={<GameIcon />}
                  label={event.game}
                  variant="outlined"
                  sx={{
                    color: 'white',
                    borderColor: alpha('#fff', 0.5),
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Event Header */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        {!event.image_url && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: { xs: 60, sm: 80 },
                height: { xs: 60, sm: 80 },
                mr: { xs: 0, sm: 3 },
                mb: { xs: 2, sm: 0 }
              }}
            >
              <EventIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
            </Avatar>
            <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                {event.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={isEventOngoing ? 'Ongoing' : event.status}
                  color={isEventOngoing ? 'warning' : getStatusColor(event.status)}
                  size="medium"
                />
                {event.game && (
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {event.game}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Organized by {event.organizer_name || 'Unknown'}
            </Typography>
          </Box>
        )}
        
        {isOrganizer && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, gap: 1, width: { xs: '100%', sm: 'auto' }, mt: { xs: 2, sm: 0 }, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/events/${eventId}/edit`)}
              fullWidth
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialog(true)}
              fullWidth
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Delete
            </Button>
          </Box>
        )}

        {/* Event Description */}
        {event.description && (
          <Typography variant="body1" sx={{ mb: 3 }}>
            {event.description}
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Event Details Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Date & Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDateRange(event.start_date, event.end_date)}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {event.location}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Participants
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {event.participant_count || 0}
                  {event.max_participants ? ` / ${event.max_participants}` : ''}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SeatIcon color="primary" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Available Seats
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {(event.seatplan.rows * event.seatplan.columns) - event.seatplan.assignments.length}
                  {` / ${event.seatplan.rows * event.seatplan.columns}`}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Seatplan */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Seatplan ({event.seatplan.rows} × {event.seatplan.columns})
              </Typography>
            
            {isRegistered ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Click on an available seat to select it. Your current seat will be highlighted.
                </Typography>
                {currentUserSeat && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="primary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Your seat: {currentUserSeat.row}-{currentUserSeat.column}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleSeatRemove}
                      disabled={selectingSeat}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
                {!hasAdminAccess && !isUserPaid && (
                  <Alert severity="warning" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    <strong>Payment Required:</strong> You must complete payment before selecting a seat. Please contact an organizer.
                  </Alert>
                )}
                {selectingSeat && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Updating seat assignment...
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Register for the event to select a seat.
              </Typography>
            )}

            {/* Venue Layout with Bar, WC, Chill, and Seatplan */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {/* Top Row: Female WC, Bar, Male WC, Chill */}
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems="center">
                  {/* Female Toilets on Left of Bar */}
                  <Grid item xs={2} sm={1.5}>
                    <VenueToiletsFemale theme={theme} />
                  </Grid>

                  {/* Bar in Center */}
                  <Grid item xs={5} sm={6}>
                    <VenueBar theme={theme} />
                  </Grid>

                  {/* Male Toilets on Right of Bar */}
                  <Grid item xs={2} sm={1.5}>
                    <VenueToiletsMale theme={theme} />
                  </Grid>

                  {/* Chillout Area on Far Right */}
                  <Grid item xs={3} sm={3}>
                    <VenueChillout theme={theme} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Seatplan grid - same width as bar section */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              mb: 2
            }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: `repeat(${event.seatplan.columns}, 1fr)`,
                gap: { xs: 0.5, sm: 1 },
                width: '100%',
                maxWidth: { xs: '100%', sm: '600px' }
              }}>
              {Array.from({ length: event.seatplan.rows * event.seatplan.columns }, (_, index) => {
                const row = Math.floor(index / event.seatplan.columns) + 1;
                const col = (index % event.seatplan.columns) + 1;
                const assignment = event.seatplan.assignments.find(
                  assignment => assignment.row === row && assignment.column === col
                );
                const isAssigned = !!assignment;
                const isCurrentUserSeat = assignment && assignment.user_id === currentUserId;
                
                let buttonVariant, buttonColor;
                if (isCurrentUserSeat) {
                  buttonVariant = "contained";
                  buttonColor = "primary";
                } else if (isAssigned) {
                  buttonVariant = "contained";
                  buttonColor = "inherit";
                } else {
                  buttonVariant = "outlined";
                  buttonColor = "primary";
                }
                
                return (
                  <Button
                    key={index}
                    variant={buttonVariant}
                    color={buttonColor}
                    size="small"
                    disabled={!isRegistered || (isAssigned && !isCurrentUserSeat) || selectingSeat}
                    onClick={() => handleSeatSelect(row, col)}
                    sx={{ 
                      minWidth: { xs: '30px', sm: '40px' }, 
                      height: { xs: '30px', sm: '40px' },
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      padding: { xs: '2px', sm: '6px' },
                      ...(isAssigned && !isCurrentUserSeat && {
                        backgroundColor: 'grey.400',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'grey.400'
                        }
                      })
                    }}
                  >
                    {row}-{col}
                  </Button>
                );
              })}
              </Box>
            </Box>

            {/* Stage below the seats */}
            <VenueStage theme={theme} />

            {/* Seat legend */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: { xs: 12, sm: 16 }, 
                  height: { xs: 12, sm: 16 }, 
                  backgroundColor: 'primary.main', 
                  borderRadius: 0.5 
                }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Your seat</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: { xs: 12, sm: 16 }, 
                  height: { xs: 12, sm: 16 }, 
                  backgroundColor: 'grey.400', 
                  borderRadius: 0.5 
                }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Occupied</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: { xs: 12, sm: 16 }, 
                  height: { xs: 12, sm: 16 }, 
                  border: '1px solid', 
                  borderColor: 'primary.main', 
                  borderRadius: 0.5 
                }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>Available</Typography>
              </Box>
            </Box>
            </CardContent>
          </Card>

          {/* Participants Section */}
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Participants ({participants.length})
                </Typography>
              </Box>

              {loadingParticipants ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : participants.length > 0 ? (
                <List>
                  {participants.map((participant, index) => {
                    const hasSeatedAssignment = event.seatplan.assignments.find(
                      assignment => assignment.user_id === participant.user_id
                    );

                    return (
                      <ListItem key={participant.user_id || `participant-${index}`} divider={index < participants.length - 1}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                            <PersonIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {participant.username || 'Unknown User'}
                              </Typography>
                              {hasSeatedAssignment && (
                                <Chip 
                                  size="small" 
                                  label={`Seat ${hasSeatedAssignment.row}-${hasSeatedAssignment.column}`}
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                                />
                              )}
                              <Chip 
                                size="small"
                                label={participant.payment_status || 'pending'}
                                color={participant.payment_status === 'paid' ? 'success' : 'warning'}
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box component="span">
                              {participant.registered_at && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                  Registered: {new Date(participant.registered_at).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        {isOrganizer && (
                          <ListItemSecondaryAction sx={{ right: { xs: 0, sm: 16 } }}>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                              <Tooltip title="Edit Payment">
                                <IconButton
                                  edge="end"
                                  onClick={() => handlePaymentEdit(participant)}
                                  disabled={updatingPayment}
                                  size="small"
                                >
                                  <PaymentIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove participant">
                                <IconButton
                                  edge="end"
                                  color="error"
                                  onClick={() => {
                                    setParticipantToRemove(participant);
                                    setRemoveParticipantDialog(true);
                                  }}
                                  disabled={removingParticipant}
                                  size="small"
                                >
                                  <RemoveParticipantIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No participants yet. Be the first to register!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Registration Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ mr: 1 }} />
                Registration
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Participants
                </Typography>
                <Typography variant="h6">
                  {event.participant_count} / {event.max_participants}
                </Typography>
              </Box>

              {user ? (
                <Box>
                  {hasEventStarted ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Registration is closed. The event has started.
                    </Alert>
                  ) : (
                    <>
                      {isRegistered ? (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckIcon sx={{ mr: 1 }} />
                              You are registered for this event
                            </Box>
                          </Alert>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<UnregisterIcon />}
                            onClick={() => setUnregisterDialog(true)}
                            disabled={registering}
                          >
                            Unregister
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<RegisterIcon />}
                          onClick={handleRegister}
                          disabled={registering || isFull}
                        >
                          {registering ? 'Registering...' : isFull ? 'Event Full' : 'Register'}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  Please log in to register for this event
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Event Stats */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Statistics
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Seats:
                </Typography>
                <Typography variant="body2">
                  {event.seatplan.rows * event.seatplan.columns}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Assigned Seats:
                </Typography>
                <Typography variant="body2">
                  {event.seatplan.assignments.length}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Available Seats:
                </Typography>
                <Typography variant="body2">
                  {(event.seatplan.rows * event.seatplan.columns) - event.seatplan.assignments.length}
                </Typography>
              </Box>

              {participants.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Payment Status
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Paid:
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      {participants.filter(p => p.payment_status === 'paid').length}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Pending:
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      {participants.filter(p => p.payment_status === 'pending').length}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Rate:
                    </Typography>
                    <Typography variant="body2">
                      {participants.length > 0 
                        ? Math.round((participants.filter(p => p.payment_status === 'paid').length / participants.length) * 100)
                        : 0}%
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* Payment Stats */}
          {participants.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Status
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid:
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {participants.filter(p => p.payment_status === 'paid').length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending:
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {participants.filter(p => p.payment_status === 'pending').length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Rate:
                  </Typography>
                  <Typography variant="body2">
                    {participants.length > 0 
                      ? Math.round((participants.filter(p => p.payment_status === 'paid').length / participants.length) * 100)
                      : 0}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Payment Edit Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Edit Payment Status
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedParticipant && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedParticipant.username}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedParticipant.email}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={paymentFormData.payment_status}
                      label="Payment Status"
                      onChange={(e) => setPaymentFormData(prev => ({ ...prev, payment_status: e.target.value }))}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="refunded">Refunded</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payment Method"
                    value={paymentFormData.payment_method}
                    onChange={(e) => setPaymentFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                    placeholder="e.g., Credit Card, PayPal, Cash"
                    helperText="Optional - how the payment was made"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentFormData.payment_amount}
                    onChange={(e) => setPaymentFormData(prev => ({ ...prev, payment_amount: e.target.value }))}
                    placeholder="0.00"
                    helperText="Optional - amount paid"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPaymentDialog(false)}
            disabled={updatingPayment}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePaymentUpdate}
            variant="contained"
            disabled={updatingPayment}
            startIcon={updatingPayment ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updatingPayment ? 'Updating...' : 'Update Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{event.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unregister Confirmation Dialog */}
      <Dialog open={unregisterDialog} onClose={() => setUnregisterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UnregisterIcon color="error" />
            Confirm Unregistration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to unregister from this event?
          </Typography>
          {currentUserSeat && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You currently have seat {currentUserSeat.row}-{currentUserSeat.column} assigned. 
                Unregistering will automatically remove your seat assignment and make it available for other participants.
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. You will need to register again if you change your mind.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUnregisterDialog(false)}
            disabled={registering}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUnregister}
            variant="contained"
            color="error"
            disabled={registering}
            startIcon={registering ? <CircularProgress size={20} /> : <UnregisterIcon />}
          >
            {registering ? 'Unregistering...' : 'Unregister'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Participant Confirmation Dialog */}
      <Dialog open={removeParticipantDialog} onClose={() => setRemoveParticipantDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RemoveParticipantIcon color="error" />
            Remove Participant
          </Box>
        </DialogTitle>
        <DialogContent>
          {participantToRemove && (
            <Box>
              <Typography paragraph>
                Are you sure you want to remove <strong>{participantToRemove.username}</strong> from this event?
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This will remove the participant from the event and automatically free up their seat assignment if they have one.
                  They will need to register again if they want to rejoin.
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRemoveParticipantDialog(false)}
            disabled={removingParticipant}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveParticipant}
            variant="contained"
            color="error"
            disabled={removingParticipant}
            startIcon={removingParticipant ? <CircularProgress size={20} /> : <RemoveParticipantIcon />}
          >
            {removingParticipant ? 'Removing...' : 'Remove Participant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default EventDetail;
